import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import loadable from '@loadable/component'
import type AdyenCheckoutType from '@adyen/adyen-web'
import type DropinElement from '@adyen/adyen-web/dist/types/components/Dropin'
import type { CoreOptions } from '@adyen/adyen-web/dist/types/core/types'
import type { BrowserInfo, PaymentMethod, PaymentAction } from '@adyen/adyen-web/dist/types/types'
import { parse, stringify } from 'querystringify'

// Actions
import { actions as payActions } from 'modules/pay/actions'
import { actions as requestActions } from 'modules/requests/actions'

// Components
import { Toast, Button, Spinner } from '@tracklib/kit'

// Hooks
import { useRequest } from 'modules/requests/hooks'

// Types
import { Dispatch } from 'types'

// Utils
import { ADYEN_CLIENT_KEY, ADYEN_ENVIRONMENT } from '@constants'

import styles from './newcardform.css'

const AdyenCheckoutLoadable = loadable.lib(() => import('@adyen/adyen-web'))
interface NewCardForm {
  paymentMethods: Array<PaymentMethod>
  onSuccess?: () => void
}

const NewCardForm = ({ paymentMethods, onSuccess }: NewCardForm) => {
  const dispatch = useDispatch() as Dispatch
  const { pathname, search } = useLocation()
  const addNewCardRequest = useRequest('ADD_NEW_CARD')
  const [dropIn, setDropIn] = useState<DropinElement | null>(null)
  const [formIsReady, setFormIsReady] = useState(false)
  const [formIsValid, setFormIsValid] = useState(false)
  const [errorText, setErrorText] = useState('')

  useEffect(() => {
    const adyenLoadPromise = AdyenCheckoutLoadable.load() as unknown as Promise<{ default: typeof AdyenCheckoutType }>
    adyenLoadPromise.then(({ default: AdyenCheckout }) => {
      const configuration: CoreOptions = {
        paymentMethodsResponse: { paymentMethods },
        clientKey: ADYEN_CLIENT_KEY,
        environment: ADYEN_ENVIRONMENT,
        locale: 'en-US',
        paymentMethodsConfiguration: {
          card: {
            hasHolderName: true,
            holderNameRequired: true,
          },
        },
        onChange: ({ isValid }: { isValid: boolean }) => {
          setFormIsValid(isValid)
        },
        onSubmit: (
          { data: { paymentMethod, browserInfo } }: { data: { paymentMethod: PaymentMethod; browserInfo: BrowserInfo } },
          dropin: DropinElement,
        ) => {
          const { modal, ...restQuery } = parse(search) as { modal: string } // eslint-disable-line
          const newSearch = stringify(restQuery)
          dispatch<{ data: { error: string; response: { action: PaymentAction } } }>(
            payActions.addNewCard(paymentMethod, browserInfo, `${pathname}${newSearch ? `?${newSearch}` : ''}`),
          ).then(response => {
            const {
              data: {
                error,
                response: { action },
              },
            } = response
            if (!error) {
              if (action) {
                dropin.handleAction(response?.data?.response?.action)
              } else {
                // To be able to add 2 cards without a refresh inbetween
                setTimeout(() => dispatch(requestActions.resetRequest('ADD_NEW_CARD')), 1500)
                if (onSuccess) {
                  onSuccess()
                }
              }
            } else {
              setErrorText(error)
            }
          })
        },
      }
      const checkout = new AdyenCheckout(configuration)
      const dropin = checkout
        .create('dropin', {
          onReady: () => {
            setFormIsReady(true)
          },
          showPayButton: false,
        })
        .mount('#dropin-container')
      setDropIn(dropin)
    })
  }, [paymentMethods]) // eslint-disable-line
  return (
    <div>
      {!formIsReady && <Spinner minHeight={150} />}
      <div style={formIsReady ? {} : { display: 'none' }}>
        <div id="dropin-container" />
        {addNewCardRequest.hasError && !addNewCardRequest.isLoading && (
          <Toast className={styles.error} description={`${errorText || 'Something went wrong.'} Please try again.`} />
        )}
        <Button
          datatesting="add_new_card"
          isDone={addNewCardRequest.isDone}
          successText="Payment method saved"
          onClick={() => dropIn?.submit()}
          isLoading={addNewCardRequest.isLoading}
          className={styles.button}
          disabled={!dropIn || !formIsValid || addNewCardRequest.isLoading || (addNewCardRequest.isDone && !addNewCardRequest.hasError)}
        >
          Add payment method
        </Button>
      </div>
    </div>
  )
}

export default NewCardForm
